import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useMemo, useState, type KeyboardEvent } from 'react';
import { api } from '@/api/client';
import type { components } from '@/api/schema';
import { AppIcon } from '@/components/AppIcon';
import { ActionBar, StatusBar } from '@/components/desktop';
import { ErrorNote, OkNote, Select, errorMessage, shortDate } from '@/components/ui';
import { formatCount, formatQuantity } from '@/format';
import { useAuth } from '@/stores/auth';
import { useFarmScope } from '@/stores/farmScope';

type Register = components['schemas']['DailyRegisterDto'];
type RegisterRow = components['schemas']['DailyRegisterRowDto'];
type RegisterBreed = components['schemas']['DailyRegisterBreedDto'];
type FeedItem = components['schemas']['FeedItemDto'];
type RegisterDay = components['schemas']['RegisterDayDto'];
type Farm = components['schemas']['FarmDto'];

/** Dead and culled as typed, for a shed or for one breed in it. */
interface Losses {
  deadCount: string;
  culledCount: string;
}

/**
 * What the person has typed for one shed, before it is saved.
 *
 * A shed holding several breeds has its dead and culled typed per breed, in
 * `breeds`; its own `deadCount` and `culledCount` are then unused, and the
 * shed's figures are the breeds' total. A shed of one breed is typed on the
 * shed's own line, as it always was.
 */
interface Draft extends Losses {
  breeds: Record<string, Losses>;
  feedSkuId: string;
  feedBags: string;
  avgWeightG: string;
  remarks: string;
}

/** Whether a shed's losses are typed breed by breed. */
function isMixed(row: RegisterRow): boolean {
  return row.breeds.length > 1;
}

function draftFor(row: RegisterRow): Draft {
  return {
    // A saved row shows what was saved; an unsaved one starts blank rather
    // than at zero, so "nothing entered" and "nothing happened" stay
    // different on screen as well as in the data.
    deadCount: row.saved ? String(row.deadCount) : '',
    culledCount: row.saved ? String(row.culledCount) : '',
    breeds: Object.fromEntries(
      row.breeds.map((b) => [
        b.placementBreedId,
        {
          deadCount: row.saved ? String(b.deadCount) : '',
          culledCount: row.saved ? String(b.culledCount) : '',
        },
      ]),
    ),
    feedSkuId: row.feedSkuId ?? '',
    feedBags: row.feedBags ?? '',
    avgWeightG: row.avgWeightG === null ? '' : String(row.avgWeightG),
    remarks: row.remarks ?? '',
  };
}

function lossesBlank(l: Losses): boolean {
  return l.deadCount.trim() === '' && l.culledCount.trim() === '';
}

/** The losses typed for a shed: its own line, or each of its breeds. */
function shedLosses(row: RegisterRow, d: Draft): Losses[] {
  return isMixed(row) ? row.breeds.map((b) => breedLosses(d, b)) : [d];
}

function breedLosses(d: Draft, b: RegisterBreed): Losses {
  return d.breeds[b.placementBreedId] ?? { deadCount: '', culledCount: '' };
}

function isBlank(row: RegisterRow, d: Draft): boolean {
  return (
    shedLosses(row, d).every(lossesBlank) &&
    d.feedBags.trim() === '' &&
    d.avgWeightG.trim() === '' &&
    d.remarks.trim() === ''
  );
}

/**
 * Dates are plain YYYY-MM-DD and are built from the *local* calendar, not from
 * `toISOString()`, which converts to UTC first and so can hand back yesterday
 * for anyone east of Greenwich.
 */
function isoOf(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function todayIsoLocal(): string {
  return isoOf(new Date());
}

function shiftDays(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number);
  const out = new Date(y!, m! - 1, d! + days);
  return isoOf(out);
}

/** The last `n` days ending today, oldest first. */
function recentDays(n: number): string[] {
  const today = todayIsoLocal();
  return Array.from({ length: n }, (_, i) => shiftDays(today, i - (n - 1)));
}

/** "Today", "Yesterday", or the plain date — how a person would say it. */
function dayLabel(date: string): string {
  const today = todayIsoLocal();
  if (date === today) return 'Today';
  if (date === shiftDays(today, -1)) return 'Yesterday';
  return shortDate(date);
}

/** Weekday initial and day number, for the strip of recent days. */
function stripLabel(date: string): { weekday: string; day: string } {
  const [y, m, d] = date.split('-').map(Number);
  const dt = new Date(y!, m! - 1, d!);
  return {
    weekday: dt.toLocaleDateString(undefined, { weekday: 'narrow' }),
    day: String(dt.getDate()),
  };
}

/** How many days the strip shows. Two weeks covers any realistic catch-up. */
const STRIP_DAYS = 14;

/**
 * A day's mortality worth raising an eyebrow at, as a percentage of the birds
 * alive that morning. Broiler flocks lose a few birds a day as a matter of
 * course; half a percent in one day is not routine.
 */
const SPIKE_PCT = 0.5;

function countOf(value: string): number {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** A shed's dead and culled for the day: its own, or its breeds' total. */
function shedCounts(row: RegisterRow, d: Draft): { dead: number; culled: number } {
  return shedLosses(row, d).reduce(
    (t, l) => ({ dead: t.dead + countOf(l.deadCount), culled: t.culled + countOf(l.culledCount) }),
    { dead: 0, culled: 0 },
  );
}

/** Birds still alive in this shed once the day's figures are counted. */
function aliveAfter(row: RegisterRow, d: Draft): number {
  const { dead, culled } = shedCounts(row, d);
  return row.birdsAliveBefore - dead - culled;
}

/**
 * The day's loss as a percentage of the birds alive that morning — for a
 * shed, or for one breed in it, from its own birds and its own losses.
 */
function dayRate(aliveBefore: number, losses: readonly Losses[]): number | null {
  if (aliveBefore <= 0) return null;
  if (losses.every(lossesBlank)) return null;
  const lost = losses.reduce((n, l) => n + countOf(l.deadCount) + countOf(l.culledCount), 0);
  return (lost / aliveBefore) * 100;
}

function DayRateCell({ aliveBefore, losses }: { aliveBefore: number; losses: readonly Losses[] }) {
  const rate = dayRate(aliveBefore, losses);
  return (
    <td className={rate !== null && rate >= SPIKE_PCT ? 'num cell--alert' : 'num dim'}>
      {rate === null ? '—' : `${rate.toFixed(2)}%`}
    </td>
  );
}

/**
 * The day's feed in kilograms, from the bags typed and what one bag holds.
 *
 * An item that holds no weight of its own is already stocked in kilograms, so
 * its quantity is the weight — the same rule the server uses to work out feed
 * per bird.
 */
function feedKgLabel(d: Draft, feedItems: FeedItem[]): string {
  const bags = Number(d.feedBags);
  if (!d.feedBags.trim() || !Number.isFinite(bags) || bags <= 0) return '—';
  const item = feedItems.find((f) => f.id === d.feedSkuId);
  if (!item) return '—';
  const pack = Number(item.kgPerUnit ?? 1);
  const kg = bags * (Number.isFinite(pack) && pack > 0 ? pack : 1);
  return formatQuantity(String(kg), 0);
}

/** What goes to the server for one shed. */
interface RowPayload {
  shedId: string;
  deadCount: number;
  culledCount: number;
  breeds?: Array<{ placementBreedId: string; deadCount: number; culledCount: number }>;
  feedSkuId?: string | null;
  feedBags?: string | null;
  avgWeightG?: number | null;
  remarks?: string | null;
  remove?: boolean;
}

/**
 * The daily register: one farm, one day, every shed holding a live flock.
 *
 * Laid out like the paper register the farm office copies from — a line per
 * shed with the deaths, the feed and the weight together — and saved in one
 * go, so a morning's typing is one action rather than twenty documents.
 *
 * Enter or the down arrow moves to the same column of the next shed, so
 * someone can type straight down a column of a paper page without reaching
 * for the mouse.
 */
export function DailyRegister() {
  const farmIds = useFarmScope((s) => s.selectedFarmIds);

  const [chosenFarm, setChosenFarm] = useState('');
  // Today, so the screen never contradicts the clock in the top bar. It used
  // to open on yesterday — on the theory the office writes the paper register
  // up the next morning — which read as a bug to anyone who glanced at the
  // date. The strip of days below makes going back one step, so the catching-up
  // case costs a click rather than a surprise.
  const [date, setDate] = useState(todayIsoLocal());

  const farms = useQuery({ queryKey: ['farms', 'list', false], queryFn: () => api.get('/api/v1/farms') });
  const liveFlocks = useQuery({
    queryKey: ['flocks', 'list', 'live-for-register'],
    queryFn: () => api.get('/api/v1/flocks', { status: 'live', take: 200, skip: 0 }),
  });

  const farmOptions = useMemo(
    () => ((farms.data ?? []) as Farm[]).filter((f) => farmIds.length === 0 || farmIds.includes(f.id)),
    [farms.data, farmIds],
  );

  // The register is one farm's page, so one has to be chosen. Until someone
  // picks, it opens on a farm that actually has birds in it — the first farm
  // by code is an arbitrary answer, and on an installation with many farms it
  // is usually an empty one, which looks like the screen is broken.
  const farmsWithFlocks = new Set(
    ((liveFlocks.data?.items ?? []) as Array<{ farmId: string }>).map((f) => f.farmId),
  );
  const defaultFarm =
    farmOptions.find((f) => farmsWithFlocks.has(f.id))?.id ?? farmOptions[0]?.id ?? '';

  // Worked out during render rather than pushed into state by an effect: a
  // farm that leaves the top bar's selection then falls away on the next
  // render instead of lingering for one frame showing another farm's sheds.
  const farmId = farmOptions.some((f) => f.id === chosenFarm) ? chosenFarm : defaultFarm;

  const register = useQuery({
    queryKey: ['daily-register', farmId, date],
    queryFn: async () => (await api.get('/api/v1/daily-register', { farmId, date })) as unknown as Register,
    enabled: farmId !== '',
  });

  const days = useMemo(() => recentDays(STRIP_DAYS), []);
  const coverage = useQuery({
    queryKey: ['daily-register', 'coverage', farmId, days[0], days[days.length - 1]],
    queryFn: async () =>
      (await api.get('/api/v1/daily-register/coverage', {
        farmId,
        from: days[0]!,
        to: days[days.length - 1]!,
      })) as unknown as RegisterDay[],
    enabled: farmId !== '',
  });

  return (
    <div className="workbench">
      <div className="workbench__main">
        {farmOptions.length === 0 && !farms.isPending && (
          <ErrorNote message="No farms are in view. Change the farm selection in the top bar." />
        )}
        {register.error && <ErrorNote message={register.error.message} />}

        {/* Nothing is drawn until the farm is settled. The grid is keyed on
            the page it shows, so rendering it against an empty farm only to
            remount a tick later threw away a render and — worse — left the
            first set of buttons detached under anyone who reached them
            quickly. */}
        {farmId === '' ? (
          <p className="muted">{farms.isPending ? 'Loading…' : ''}</p>
        ) : (
        <RegisterGrid
          key={`${farmId}:${date}`}
          farmId={farmId}
          date={date}
          rows={register.data?.rows ?? []}
          feedItems={register.data?.feedItems ?? []}
          loading={register.isPending && farmId !== ''}
          farmOptions={farmOptions}
          onFarmChange={setChosenFarm}
          onDateChange={setDate}
          days={days}
          coverage={coverage.data ?? []}
          onSaved={() => void coverage.refetch()}
        />
        )}
      </div>
    </div>
  );
}

/**
 * The last fortnight at a glance: which days are written up, which are part
 * done, and which are still blank.
 *
 * Without it the only way to find a gap is to open each day in turn, and a
 * missed day is invisible until someone notices the flock's figures look
 * wrong weeks later.
 */
function DayStrip({
  days,
  coverage,
  date,
  onPick,
}: {
  days: string[];
  coverage: RegisterDay[];
  date: string;
  onPick: (date: string) => void;
}) {
  // The strip is a convenience; a malformed or missing answer must leave the
  // register itself perfectly usable rather than taking the screen down.
  const by = new Map(
    (Array.isArray(coverage) ? coverage : []).map((c) => [c.date, c]),
  );

  return (
    <div className="daystrip" role="group" aria-label="Recent days">
      {days.map((d) => {
        const c = by.get(d);
        const expected = c?.expected ?? 0;
        const entered = c?.entered ?? 0;
        const state =
          expected === 0
            ? 'none'
            : entered === 0
              ? 'empty'
              : entered < expected
                ? 'part'
                : 'done';
        const { weekday, day } = stripLabel(d);
        const words =
          state === 'none'
            ? 'no flock that day'
            : state === 'done'
              ? `all ${expected} shed(s) written up`
              : state === 'part'
                ? `${entered} of ${expected} sheds written up`
                : `${expected} shed(s) still to write up`;

        return (
          <button
            key={d}
            type="button"
            className={`daystrip__day daystrip__day--${state}${d === date ? ' daystrip__day--on' : ''}`}
            aria-current={d === date ? 'date' : undefined}
            title={`${dayLabel(d)} — ${words}`}
            onClick={() => onPick(d)}
          >
            <span className="daystrip__weekday">{weekday}</span>
            <span className="daystrip__num">{day}</span>
          </button>
        );
      })}
    </div>
  );
}

function RegisterGrid({
  farmId,
  date,
  rows,
  feedItems,
  loading,
  farmOptions,
  onFarmChange,
  onDateChange,
  days,
  coverage,
  onSaved,
}: {
  farmId: string;
  date: string;
  rows: RegisterRow[];
  feedItems: FeedItem[];
  loading: boolean;
  farmOptions: Farm[];
  onFarmChange: (id: string) => void;
  onDateChange: (date: string) => void;
  days: string[];
  coverage: RegisterDay[];
  onSaved: () => void;
}) {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  // Initialised straight from the rows this grid was built for. The `key` on
  // it means a new page gets a new grid, so there is nothing to synchronise.
  const [drafts, setDrafts] = useState<Record<string, Draft>>(() =>
    Object.fromEntries(rows.map((r) => [r.shedId, draftFor(r)])),
  );
  const [note, setNote] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const mayWrite = can('ops.daily.create');

  /**
   * Takes the whole row, not just its id, so an untouched row falls back to
   * what was saved for it rather than to a blank.
   *
   * The grid can mount before its rows arrive — the query key changes with
   * the farm and the day, so `data` is briefly undefined — which leaves
   * `drafts` empty while the table renders from `draftFor`. Falling back to a
   * blank draft here meant typing one cell of an already-written row silently
   * cleared that row's feed, weight and culled count, and the save then wrote
   * the blanks.
   */
  function set(row: RegisterRow, patch: Partial<Draft>) {
    setDrafts((d) => ({ ...d, [row.shedId]: { ...(d[row.shedId] ?? draftFor(row)), ...patch } }));
    setNote(null);
  }

  /** One breed's dead or culled, in a shed holding several. */
  function setBreed(row: RegisterRow, breed: RegisterBreed, patch: Partial<Losses>) {
    setDrafts((d) => {
      const draft = d[row.shedId] ?? draftFor(row);
      return {
        ...d,
        [row.shedId]: {
          ...draft,
          breeds: { ...draft.breeds, [breed.placementBreedId]: { ...breedLosses(draft, breed), ...patch } },
        },
      };
    });
    setNote(null);
  }

  function draftOf(r: RegisterRow): Draft {
    return drafts[r.shedId] ?? draftFor(r);
  }

  function isChanged(r: RegisterRow): boolean {
    return JSON.stringify(draftOf(r)) !== JSON.stringify(draftFor(r));
  }

  const changed = rows.filter(isChanged);

  /**
   * Enter and the arrow keys move down and up the same column, which is how a
   * paper register is read — one column at a time, not one row.
   */
  function onGridKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    // Ctrl+S is what anyone typing into a grid reaches for, and the browser's
    // own "save page" is never what they meant.
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
      e.preventDefault();
      if (mayWrite && !saving && changed.length > 0) void save();
      return;
    }
    if (e.key !== 'Enter' && e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const target = e.target as HTMLElement;
    const column = target.dataset.column;
    if (!column) return;

    // The column's cells in the order they are drawn, so a shed's breed lines
    // are stepped through in turn before the next shed.
    const cells = [...e.currentTarget.querySelectorAll<HTMLElement>(`[data-column="${column}"]`)];
    const at = cells.indexOf(target);
    const cell = cells[e.key === 'ArrowUp' ? at - 1 : at + 1];
    if (at >= 0 && cell) {
      e.preventDefault();
      cell.focus();
    }
  }

  async function save() {
    setProblem(null);
    setNote(null);

    const toSend = rows.flatMap<RowPayload>((r) => {
      if (!isChanged(r)) return [];
      const d = draftOf(r);

      // A saved row cleared back to blank is a removal; a never-saved row left
      // blank is simply not sent.
      if (isBlank(r, d)) {
        return r.saved ? [{ shedId: r.shedId, deadCount: 0, culledCount: 0, remove: true }] : [];
      }

      const { dead, culled } = shedCounts(r, d);
      return [
        {
          shedId: r.shedId,
          deadCount: dead,
          culledCount: culled,
          // Every breed, even one with nothing typed: the server is told the
          // whole of the day, not left to assume the rest were zero.
          ...(r.breeds.length > 0
            ? {
                breeds: r.breeds.map((b) => {
                  const l = isMixed(r) ? breedLosses(d, b) : d;
                  return {
                    placementBreedId: b.placementBreedId,
                    deadCount: countOf(l.deadCount),
                    culledCount: countOf(l.culledCount),
                  };
                }),
              }
            : {}),
          feedSkuId: d.feedSkuId || null,
          feedBags: d.feedBags.trim() || null,
          avgWeightG: d.avgWeightG.trim() ? Number(d.avgWeightG) : null,
          remarks: d.remarks.trim() || null,
        },
      ];
    });

    if (toSend.length === 0) {
      setProblem('Nothing has changed, so there is nothing to save.');
      return;
    }

    try {
      setSaving(true);
      const result = (await api.put('/api/v1/daily-register', { farmId, date, rows: toSend })) as {
        saved: number;
        removed: number;
      };
      await queryClient.invalidateQueries({ queryKey: ['daily-register'] });
      // The flock figures and the shed list are built from these rows, so
      // both are stale now.
      await queryClient.invalidateQueries({ queryKey: ['flocks'] });
      await queryClient.invalidateQueries({ queryKey: ['sheds'] });
      // So the strip of days marks this one written up straight away.
      onSaved();
      setNote(
        result.removed > 0
          ? `Saved ${result.saved} shed(s), removed ${result.removed}.`
          : `Saved ${result.saved} shed(s) for ${dayLabel(date).toLowerCase()}.`,
      );
    } catch (e) {
      setProblem(errorMessage(e, 'Could not save the register'));
    } finally {
      setSaving(false);
    }
  }

  const tallied = rows.reduce(
    (t, r) => {
      const d = draftOf(r);
      const { dead, culled } = shedCounts(r, d);
      return {
        dead: t.dead + dead,
        culled: t.culled + culled,
        bags: t.bags + countOf(d.feedBags),
        alive: t.alive + r.birdsAliveBefore,
      };
    },
    { dead: 0, culled: 0, bags: 0, alive: 0 },
  );
  // A count on its own says nothing: the same number of deaths is routine on
  // a big farm and alarming on a small one.
  const totals = {
    ...tallied,
    rate:
      tallied.alive > 0 && tallied.dead + tallied.culled > 0
        ? ((tallied.dead + tallied.culled) / tallied.alive) * 100
        : null,
  };

  return (
    <>
      <ActionBar
        actions={[
          {
            label: saving ? 'Saving…' : 'Save register',
            icon: 'post',
            primary: true,
            onSelect: () => void save(),
            allowed: mayWrite,
            enabled: !saving && changed.length > 0,
            reason: changed.length === 0 ? 'Nothing has changed yet' : 'Saving…',
          },
        ]}
      >
        <Select
          label="Farm"
          value={farmId}
          onChange={onFarmChange}
          options={farmOptions.map((f) => ({ value: f.id, label: `${f.code} — ${f.nameEn}` }))}
          placeholder=""
        />
        <label className="field">
          <span className="field__label">Date</span>
          <input
            className="field__input"
            type="date"
            value={date}
            max={todayIsoLocal()}
            onChange={(e) => onDateChange(e.target.value)}
          />
        </label>
      </ActionBar>

      <div className="daybar">
        <button
          type="button"
          className="btn btn--sm"
          onClick={() => onDateChange(shiftDays(date, -1))}
          aria-label="The day before"
          title="The day before"
        >
          ‹
        </button>
        <strong className="daybar__label">
          {dayLabel(date)}
          {dayLabel(date) !== shortDate(date) && (
            <span className="dim"> · {shortDate(date)}</span>
          )}
        </strong>
        <button
          type="button"
          className="btn btn--sm"
          disabled={date >= todayIsoLocal()}
          onClick={() => onDateChange(shiftDays(date, 1))}
          aria-label="The day after"
          title={date >= todayIsoLocal() ? 'Today is as far as the register goes' : 'The day after'}
        >
          ›
        </button>
        <button
          type="button"
          className="btn btn--sm"
          disabled={date === todayIsoLocal()}
          onClick={() => onDateChange(todayIsoLocal())}
        >
          Today
        </button>

        <DayStrip days={days} coverage={coverage} date={date} onPick={onDateChange} />

        <span className="daybar__key dim">
          <span className="daystrip__swatch daystrip__swatch--done" /> written up
          <span className="daystrip__swatch daystrip__swatch--part" /> part done
          <span className="daystrip__swatch daystrip__swatch--empty" /> still blank
        </span>
      </div>

      {note && <OkNote message={note} />}
      {problem && <ErrorNote message={problem} />}

      {loading ? (
        <p className="muted">Loading…</p>
      ) : rows.length === 0 && farmId !== '' ? (
        <p className="muted">
          No shed on this farm had a live flock on {shortDate(date)}. Place a flock under Farm Manager &rsaquo; Flocks,
          or pick another day.
        </p>
      ) : (
        <div onKeyDown={onGridKeyDown}>
          <table className="table">
            <thead>
              <tr>
                <th>Shed</th>
                <th>Flock</th>
                <th className="num">Day</th>
                <th className="num">Alive before</th>
                <th className="num">Dead</th>
                <th className="num">Culled</th>
                <th className="num">Alive after</th>
                <th className="num">Day mortality</th>
                <th>Feed</th>
                <th className="num">Bags used</th>
                <th className="num">Feed (kg)</th>
                <th className="num">Avg weight (g)</th>
                <th>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const d = draftOf(r);
                const mixed = isMixed(r);
                const counts = shedCounts(r, d);
                return (
                  <Fragment key={r.shedId}>
                    <tr className={mixed ? 'register__mixed' : undefined}>
                      <td>
                        <span className="withicon">
                          <AppIcon kind="shed" size={14} />
                          <span className="mono">{r.shedCode}</span>
                        </span>
                        {r.breeds.length === 1 && <div className="register__breed">{r.breeds[0]!.breed}</div>}
                        {mixed && <div className="register__breed">{r.breeds.length} breeds</div>}
                      </td>
                      <td className="dim">
                        <span className="mono">{r.flockCode}</span>
                      </td>
                      <td className="num">{r.dayNo}</td>
                      <td className="num">{formatCount(r.birdsAliveBefore)}</td>
                      {mixed ? (
                        // Typed per breed on the lines below; this is their total.
                        <>
                          <td className="num" aria-label={`Dead in ${r.shedCode}, all breeds`}>
                            {shedLosses(r, d).every(lossesBlank) ? '—' : formatCount(counts.dead)}
                          </td>
                          <td className="num" aria-label={`Culled in ${r.shedCode}, all breeds`}>
                            {shedLosses(r, d).every(lossesBlank) ? '—' : formatCount(counts.culled)}
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="num">
                            <LossInput
                              label={`Dead in ${r.shedCode}`}
                              column="dead"
                              shedId={r.shedId}
                              disabled={!mayWrite}
                              value={d.deadCount}
                              onChange={(deadCount) => set(r, { deadCount })}
                            />
                          </td>
                          <td className="num">
                            <LossInput
                              label={`Culled in ${r.shedCode}`}
                              column="culled"
                              shedId={r.shedId}
                              disabled={!mayWrite}
                              value={d.culledCount}
                              onChange={(culledCount) => set(r, { culledCount })}
                            />
                          </td>
                        </>
                      )}
                      {/* Worked out as it is typed. A supervisor is watching for
                          a spike, and 40 of 11,000 reads very differently from
                          40 of 400 — the raw count alone does not say which. */}
                      <td className="num">{formatCount(aliveAfter(r, d))}</td>
                      <DayRateCell aliveBefore={r.birdsAliveBefore} losses={shedLosses(r, d)} />
                      <td>
                        <select
                          className="field__input"
                          aria-label={`Feed for ${r.shedCode}`}
                          data-column="feed"
                          data-shed={r.shedId}
                          disabled={!mayWrite}
                          value={d.feedSkuId}
                          onChange={(e) => set(r, { feedSkuId: e.target.value })}
                        >
                          <option value="">None</option>
                          {feedItems.map((f) => (
                            <option key={f.id} value={f.id}>
                              {f.code} — {f.nameEn}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="num">
                        <input
                          className="field__input"
                          type="number"
                          min={0}
                          step="any"
                          inputMode="decimal"
                          aria-label={`Bags used in ${r.shedCode}`}
                          data-column="bags"
                          data-shed={r.shedId}
                          disabled={!mayWrite}
                          value={d.feedBags}
                          onChange={(e) => set(r, { feedBags: e.target.value })}
                        />
                      </td>
                      {/* Bags are what the shed counts; kilograms are what the
                          feed costs and what FCR divides. Showing both saves
                          doing the pack-size sum in your head. */}
                      <td className="num dim">{feedKgLabel(d, feedItems)}</td>
                      <td className="num">
                        <input
                          className="field__input"
                          type="number"
                          min={0}
                          inputMode="numeric"
                          aria-label={`Average weight in ${r.shedCode}`}
                          data-column="weight"
                          data-shed={r.shedId}
                          disabled={!mayWrite}
                          value={d.avgWeightG}
                          onChange={(e) => set(r, { avgWeightG: e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          className="field__input"
                          aria-label={`Remarks for ${r.shedCode}`}
                          data-column="remarks"
                          data-shed={r.shedId}
                          disabled={!mayWrite}
                          value={d.remarks}
                          onChange={(e) => set(r, { remarks: e.target.value })}
                        />
                      </td>
                    </tr>
                    {mixed &&
                      r.breeds.map((b) => {
                        const l = breedLosses(d, b);
                        const label = `${b.breed} in ${r.shedCode}`;
                        return (
                          <tr key={b.placementBreedId} className="table__breedrow">
                            <td colSpan={3}>{b.breed}</td>
                            <td className="num">{formatCount(b.birdsAliveBefore)}</td>
                            <td className="num">
                              <LossInput
                                label={`Dead ${label}`}
                                column="dead"
                                shedId={r.shedId}
                                disabled={!mayWrite}
                                value={l.deadCount}
                                onChange={(deadCount) => setBreed(r, b, { deadCount })}
                              />
                            </td>
                            <td className="num">
                              <LossInput
                                label={`Culled ${label}`}
                                column="culled"
                                shedId={r.shedId}
                                disabled={!mayWrite}
                                value={l.culledCount}
                                onChange={(culledCount) => setBreed(r, b, { culledCount })}
                              />
                            </td>
                            <td className="num">
                              {formatCount(b.birdsAliveBefore - countOf(l.deadCount) - countOf(l.culledCount))}
                            </td>
                            <DayRateCell aliveBefore={b.birdsAliveBefore} losses={[l]} />
                            <td colSpan={5} />
                          </tr>
                        );
                      })}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <StatusBar>
        <span>{rows.length === 1 ? '1 shed' : `${rows.length} sheds`}</span>
        <span>
          {formatCount(totals.dead)} dead · {formatCount(totals.culled)} culled
          {totals.rate !== null && ` · ${totals.rate.toFixed(2)}% of the farm`}
        </span>
        <span>{formatQuantity(String(totals.bags), 2)} bags</span>
        {/* The one bit of behaviour nobody would guess at. */}
        <span className="dim">Enter moves down a column · Ctrl+S saves</span>
        <span>{changed.length === 0 ? 'Nothing to save' : `${changed.length} shed(s) changed — not saved yet`}</span>
      </StatusBar>
    </>
  );
}

/** A dead or culled box in the grid, for a shed or one breed in it. */
function LossInput({
  label,
  column,
  shedId,
  disabled,
  value,
  onChange,
}: {
  label: string;
  column: 'dead' | 'culled';
  shedId: string;
  disabled: boolean;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <input
      className="field__input"
      type="number"
      min={0}
      inputMode="numeric"
      aria-label={label}
      data-column={column}
      data-shed={shedId}
      disabled={disabled}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}
